import { AuthGuard } from '@/app/core/guards/auth.guard';
import { AdminGuard } from '@/app/core/guards/admin.guard';
import { GuestGuard } from '@/app/core/guards/guest.guard';
import { LayoutsEnum } from '@/app/shared/enums/layouts.enum';
import { ProfileGuard } from '@/app/core/guards/profile.guard';
import { UserResolver } from '@/app/features/impersonate/resolvers';
import { NfcRedirectGuard } from '@/app/core/guards/nfc-redirect.guard';

import { Routes } from '@angular/router';

export const routes: Routes = [
  {
    path: 'auth',
    canMatch: [NfcRedirectGuard],
    children: [
      {
        path: 'login',
        canMatch: [GuestGuard],
        loadComponent: () => import('./pages/auth/login/login.component').then((mod) => mod.LoginComponent),
        data: {
          layout: LayoutsEnum.Auth,
        },
      },
      {
        path: 'registration',
        canMatch: [GuestGuard],
        loadComponent: () =>
          import('./pages/auth/registration/registration.component').then((c) => c.RegistrationComponent),
        data: {
          layout: LayoutsEnum.Auth,
        },
      },
      {
        path: 'registration/link',
        canMatch: [GuestGuard],
        loadComponent: () =>
          import('./pages/auth/registration-complete/registration-complete.component').then(
            (c) => c.RegistrationCompleteComponent,
          ),
        data: {
          layout: LayoutsEnum.Auth,
        },
      },
      {
        path: ':provider/callback',
        canMatch: [GuestGuard],
        loadComponent: () => import('./pages/auth/callback/callback.component').then((mod) => mod.CallbackComponent),
        data: {
          layout: LayoutsEnum.Auth,
        },
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./pages/auth/forgot-password/forgot-password.component').then((mod) => mod.ForgotPasswordComponent),
        data: {
          layout: LayoutsEnum.Auth,
        },
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./pages/auth/reset-password/reset-password.component').then((mod) => mod.ResetPasswordComponent),
        data: {
          layout: LayoutsEnum.Auth,
        },
      },
      {
        path: '**',
        redirectTo: 'login',
      },
    ],
  },

  {
    path: '',
    canMatch: [],
    children: [
      {
        path: 'design',
        canMatch: [ProfileGuard, AuthGuard],
        loadComponent: () => import('./pages/design/design.component').then((mod) => mod.DesignComponent),
        data: {
          layout: LayoutsEnum.Default,
        },
      },
      {
        path: 'redirect',
        canMatch: [ProfileGuard, AuthGuard],
        loadComponent: () => import('./pages/redirect/redirect.component').then((mod) => mod.RedirectComponent),
        data: {
          layout: LayoutsEnum.Default,
        },
      },
      {
        path: 'analytics',
        canMatch: [ProfileGuard, AuthGuard],
        loadComponent: () => import('./pages/analytics/analytics.component').then((mod) => mod.AnalyticsComponent),
        data: {
          layout: LayoutsEnum.Default,
        },
      },
      {
        path: 'products',
        canMatch: [ProfileGuard, AuthGuard],
        loadComponent: () => import('./pages/products/products.component').then((mod) => mod.ProductsComponent),
        data: {
          layout: LayoutsEnum.Default,
        },
      },
      // {
      //   path: 'subscription',
      //   canMatch: [ProfileGuard, AuthGuard],
      //   loadComponent: () =>
      //     import('./pages/subscription/subscription.component').then((mod) => mod.SubscriptionComponent),
      //   data: {
      //     layout: LayoutsEnum.Default,
      //   },
      // },
      // {
      //   path: 'subscription/checkout/:status',
      //   canMatch: [ProfileGuard, AuthGuard],
      //   loadComponent: () =>
      //     import('./pages/subscription/components/payment-callback/payment-callback.component').then(
      //       (mod) => mod.PaymentCallbackComponent,
      //     ),
      //   data: {
      //     layout: LayoutsEnum.Default,
      //   },
      // },
      {
        path: 'affiliate-link',
        canMatch: [ProfileGuard, AuthGuard],
        loadComponent: () =>
          import('./pages/affiliate-link/affiliate-link.component').then((mod) => mod.AffiliateLinkComponent),
        data: {
          layout: LayoutsEnum.Default,
        },
      },
      {
        path: 'settings',
        canMatch: [ProfileGuard, AuthGuard],
        loadComponent: () => import('./pages/settings/settings.component').then((mod) => mod.SettingsComponent),
        data: {
          layout: LayoutsEnum.Default,
        },
      },

      {
        path: 'admin',
        canMatch: [ProfileGuard, AuthGuard, AdminGuard],
        data: {
          layout: LayoutsEnum.Admin,
        },
        children: [
          {
            path: 'analytics',
            loadComponent: () =>
              import('./pages/admin/analytics/analytics.component').then((mod) => mod.AnalyticsComponent),
            data: {
              layout: LayoutsEnum.Admin,
            },
          },

          {
            path: 'users',
            data: {
              layout: LayoutsEnum.Admin,
            },
            children: [
              {
                path: '',
                loadComponent: () => import('./pages/admin/users/users.component').then((mod) => mod.UsersComponent),
                data: {
                  layout: LayoutsEnum.Admin,
                },
              },
              {
                path: ':userId',
                resolve: {
                  userResponse: UserResolver,
                },
                children: [
                  {
                    path: 'design',
                    loadComponent: () => import('./pages/design/design.component').then((mod) => mod.DesignComponent),
                    data: {
                      layout: LayoutsEnum.AdminDefault,
                    },
                  },
                  {
                    path: 'analytics',
                    loadComponent: () =>
                      import('./pages/analytics/analytics.component').then((mod) => mod.AnalyticsComponent),
                    data: {
                      layout: LayoutsEnum.AdminDefault,
                    },
                  },
                  {
                    path: 'products',
                    loadComponent: () =>
                      import('./pages/products/products.component').then((mod) => mod.ProductsComponent),
                    data: {
                      layout: LayoutsEnum.AdminDefault,
                    },
                  },
                  {
                    path: 'affiliate-link',
                    loadComponent: () =>
                      import('./pages/affiliate-link/affiliate-link.component').then(
                        (mod) => mod.AffiliateLinkComponent,
                      ),
                    data: {
                      layout: LayoutsEnum.AdminDefault,
                    },
                  },
                  {
                    path: 'redirect',
                    loadComponent: () =>
                      import('./pages/redirect/redirect.component').then((mod) => mod.RedirectComponent),
                    data: {
                      layout: LayoutsEnum.AdminDefault,
                    },
                  },
                ],
              },
            ],
          },

          {
            path: 'branding',
            loadComponent: () =>
              import('./pages/admin/branding/branding.component').then((mod) => mod.BrandingComponent),
            data: {
              layout: LayoutsEnum.Admin,
            },
          },

          {
            path: 'settings',
            loadComponent: () =>
              import('./pages/admin/settings/settings.component').then((mod) => mod.SettingsComponent),
            data: {
              layout: LayoutsEnum.Admin,
            },
          },

          {
            path: '**',
            redirectTo: 'analytics',
          },
        ],
      },

      {
        path: ':id',
        title: 'Preview',
        canMatch: [],
        loadComponent: () =>
          import('./pages/design/components/preview/preview.component').then((mod) => mod.PreviewComponent),
        data: {
          layout: LayoutsEnum.Preview,
        },
      },

      {
        path: '**',
        redirectTo: 'design',
      },
    ],
  },
];
